import React, { useState } from 'react'
import {
  Camera,
  CheckCircle,
  Edit,
  Plus,
  Trash2,
  ExternalLink,
  Instagram,
  Youtube,
  Save,
  Shield,
  Star,
  Award
} from 'lucide-react'
import { mockCreators } from '../../data/mockData'

const creator = mockCreators[0]

function CreatorProfile() {
  const [isEditing, setIsEditing] = useState(false)
  const [profile, setProfile] = useState({
    title: creator.title,
    bio: creator.bio,
    specialty: creator.specialty,
    location: creator.location,
    baseRates: {
      reel: 5000,
      story: 1000,
      post: 3000,
      video: 8000,
    },
  })

  const handleSave = () => {
    setIsEditing(false)
    // Save profile changes
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-500">Manage your public profile and rates</p>
        </div>
        {isEditing ? (
          <div className="flex gap-3">
            <button onClick={() => setIsEditing(false)} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </button>
          </div>
        ) : (
          <button onClick={() => setIsEditing(true)} className="btn-secondary">
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Profile Header Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {/* Avatar */}
          <div className="relative">
            <img
              src={creator.avatar}
              alt={creator.name}
              className="w-28 h-28 rounded-2xl object-cover"
            />
            {isEditing && (
              <button className="absolute bottom-2 right-2 w-8 h-8 bg-hf-teal-600 text-white rounded-lg flex items-center justify-center hover:bg-hf-teal-700">
                <Camera className="w-4 h-4" />
              </button>
            )}
            {creator.verified && (
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-hf-teal-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{creator.name}</h2>
              <span className="badge bg-purple-100 text-purple-700 capitalize">{creator.tier}</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={profile.title}
                onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                className="input mb-3"
              />
            ) : (
              <p className="text-gray-600 mb-3">{profile.title}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center">
                <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                {creator.rating} rating
              </span>
              <span>{creator.campaignsCompleted} campaigns</span>
              <span>{creator.onTimeRate}% on-time</span>
            </div>
          </div>

          {/* Verification Badges */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm">
              <Shield className="w-4 h-4 mr-2" />
              SOFI Verified
            </div>
            <div className="flex items-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <Award className="w-4 h-4 mr-2" />
              Compliance Certified
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
        {isEditing ? (
          <textarea
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            className="input min-h-[120px]"
          />
        ) : (
          <p className="text-gray-600">{profile.bio}</p>
        )}
      </div>

      {/* Specialties */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Specialties</h3>
        <div className="flex flex-wrap gap-2">
          {profile.specialty.map((spec, i) => (
            <span
              key={i}
              className="px-4 py-2 bg-hf-teal-50 text-hf-teal-700 rounded-full text-sm font-medium flex items-center"
            >
              {spec}
              {isEditing && (
                <button className="ml-2 text-hf-teal-400 hover:text-hf-teal-600">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {isEditing && (
            <button className="px-4 py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-full text-sm hover:border-hf-teal-300 hover:text-hf-teal-600 flex items-center">
              <Plus className="w-4 h-4 mr-1" />
              Add Specialty
            </button>
          )}
        </div>
      </div>

      {/* Connected Platforms */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connected Platforms</h3>
        <div className="space-y-4">
          {Object.entries(creator.platforms).map(([platform, data]) => (
            <div key={platform} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-4 shadow-sm">
                  {platform === 'instagram' && <Instagram className="w-5 h-5 text-pink-600" />}
                  {platform === 'youtube' && <Youtube className="w-5 h-5 text-red-600" />}
                  {platform === 'tiktok' && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  )}
                  {platform === 'linkedin' && (
                    <svg className="w-5 h-5 text-blue-700" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900 capitalize">{platform}</p>
                  <p className="text-sm text-gray-500">@{creator.name.toLowerCase().replace(/\s+/g, '').replace(/\./g, '')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-900">{(data.followers / 1000).toFixed(0)}K</p>
                <p className="text-sm text-gray-500">{data.engagement}% engagement</p>
              </div>
            </div>
          ))}
          <button className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-hf-teal-300 hover:text-hf-teal-600 flex items-center justify-center">
            <Plus className="w-5 h-5 mr-2" />
            Connect Platform
          </button>
        </div>
      </div>

      {/* Rate Card */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Card</h3>
        <p className="text-sm text-gray-500 mb-4">Set your base rates for different content types. SOFI uses these as a starting point for campaign matching.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(profile.baseRates).map(([type, rate]) => (
            <div key={type} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <span className="font-medium text-gray-900 capitalize">{type}</span>
              {isEditing ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={rate}
                    onChange={(e) => setProfile({
                      ...profile,
                      baseRates: { ...profile.baseRates, [type]: parseInt(e.target.value) }
                    })}
                    className="w-28 pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-right"
                  />
                </div>
              ) : (
                <span className="font-semibold text-gray-900">${rate.toLocaleString()}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Training */}
      <div className="card bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-2">
              <Shield className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Compliance Training Complete</h3>
            </div>
            <p className="text-gray-600 mb-4">
              You've completed SOFI's healthcare marketing compliance training. Your certification is valid through December 2026.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-600 font-medium">Score: 94%</span>
              <span className="text-gray-500">Completed: Jan 15, 2026</span>
            </div>
          </div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreatorProfile
